import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from "react";
import { api } from "../api/client";

interface User {
	id: string;
	email: string;
	name: string;
	picture: string;
}

interface AuthContextType {
	user: User | null;
	loading: boolean;
	authenticated: boolean;
	logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
	user: null,
	loading: true,
	authenticated: false,
	logout: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const [user, setUser] = useState<User | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const checkAuth = async () => {
			try {
				const { data } = await api.auth.me.get();
				if (
					data &&
					typeof data === "object" &&
					"authenticated" in data &&
					data.authenticated &&
					"user" in data &&
					data.user
				) {
					setUser(data.user as User);
				}
			} catch (error) {
				console.error("Auth check failed:", error);
			} finally {
				setLoading(false);
			}
		};
		checkAuth();
	}, []);

	const logout = async () => {
		try {
			await api.auth.logout.post();
			setUser(null);
		} catch (error) {
			console.error("Logout failed:", error);
		}
	};

	return (
		<AuthContext.Provider
			value={{
				user,
				loading,
				authenticated: !!user,
				logout,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};
